import React, { useState } from 'react';
import { LayoutDashboard, Package, Truck, Store, Users, FileText, ShoppingCart, BarChart3, Settings, LogOut, ChevronDown, ChevronRight, X } from 'lucide-react';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
}
interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: string;
  submenu?: {
    id: string;
    label: string;
    page: string;
    subPage?: string;
  }[];
}
export function Sidebar({
  currentPage,
  onNavigate,
  onLogout,
  isOpen,
  onClose
}: SidebarProps) {
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['warehouse', 'distribution', 'reports']);
  const menuItems: MenuItem[] = [{
    id: 'dashboard',
    label: 'Dasbor',
    icon: <LayoutDashboard size={20} data-id="element-46" />
  }, {
    id: 'warehouse',
    label: 'Gudang & Stok',
    icon: <Package size={20} data-id="element-47" />,
    submenu: [{
      id: 'stock',
      label: 'Daftar Stok',
      page: 'warehouse'
    }, {
      id: 'commodities',
      label: 'Komoditas',
      page: 'warehouse',
      subPage: 'commodities'
    }, {
      id: 'inbound',
      label: 'Penerimaan Barang',
      page: 'warehouse',
      subPage: 'inbound'
    }, {
      id: 'outbound',
      label: 'Pengeluaran Barang',
      page: 'warehouse',
      subPage: 'outbound'
    }]
  }, {
    id: 'distribution',
    label: 'Distribusi',
    icon: <Truck size={20} data-id="element-48" />,
    submenu: [{
      id: 'orders',
      label: 'Order Distribusi',
      page: 'distribution'
    }, {
      id: 'pod',
      label: 'POD Review',
      page: 'distribution',
      subPage: 'pod'
    }]
  }, {
    id: 'programs',
    label: 'Program',
    icon: <FileText size={20} data-id="element-49" />,
    badge: '6'
  }, {
    id: 'procurement',
    label: 'Pengadaan',
    icon: <ShoppingCart size={20} data-id="element-50" />
  }, {
    id: 'sphp',
    label: 'SPHP Retail',
    icon: <Store size={20} data-id="element-51" />,
    submenu: [{
      id: 'outlets',
      label: 'Outlet Registry',
      page: 'sphp'
    }, {
      id: 'monitoring',
      label: 'Monitoring Harga',
      page: 'sphp',
      subPage: 'monitoring'
    }]
  }, {
    id: 'bantuan',
    label: 'Bantuan Pangan',
    icon: <Users size={20} data-id="element-52" />
  }, {
    id: 'reports',
    label: 'Laporan',
    icon: <BarChart3 size={20} data-id="element-53" />,
    submenu: [{
      id: 'stock-report',
      label: 'Laporan Stok',
      page: 'reports'
    }, {
      id: 'audit',
      label: 'Audit Trail',
      page: 'reports',
      subPage: 'audit'
    }]
  }];
  const toggleSubmenu = (menuId: string) => {
    setExpandedMenus(prev => prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]);
  };
  const handleMenuClick = (item: MenuItem) => {
    if (item.submenu) {
      toggleSubmenu(item.id);
    } else {
      onNavigate(item.id);
    }
  };
  const handleSubmenuClick = (submenu: any) => {
    if (submenu.subPage) {
      onNavigate(submenu.page, submenu.subPage);
    } else {
      onNavigate(submenu.page);
    }
  };
  const isMenuActive = (menuId: string) => {
    return currentPage === menuId;
  };
  const isSubmenuActive = (submenu: any) => {
    return currentPage === submenu.page && (!submenu.subPage || window.location.hash.includes(submenu.subPage));
  };
  return <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 flex flex-col z-40 transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:sticky shadow-lg lg:shadow-none`} data-id="element-54">
      {/* Header - Logo Area */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0 bg-white" data-id="element-55">
        <div className="flex items-center gap-3" data-id="element-56">
          <img src="https://cdn.magicpatterns.com/uploads/gKwFj36NtZysn8CWxJT9Nn/Logo-BULOG_Web-2048x650.png" alt="Logo BULOG" className="h-8 w-auto object-contain" data-id="element-57" />
          <div className="hidden xl:block" data-id="element-58">
            <p className="text-xs font-semibold text-bulog-blue" data-id="element-59">BULOG</p>
            <p className="text-[10px] text-gray-500" data-id="element-60">Enterprise System</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Tutup menu" data-id="element-61">
          <X size={20} className="text-gray-600" data-id="element-62" />
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-3 px-3 overflow-y-auto" data-id="element-63">
        <div className="space-y-1" data-id="element-64">
          {menuItems.map(item => <div key={item.id} data-id="element-65">
              {/* Main Menu Item */}
              <button type="button" onClick={() => handleMenuClick(item)} className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${isMenuActive(item.id) && !item.submenu ? 'bg-bulog-blue text-white shadow-md' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-66">
                <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-67">
                  <span className={`flex-shrink-0 ${isMenuActive(item.id) && !item.submenu ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}`} data-id="element-68">
                    {item.icon}
                  </span>
                  <span className="truncate text-left" data-id="element-69">{item.label}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-70">
                  {item.badge && <span className="px-2 py-0.5 bg-bulog-orange text-white text-xs font-bold rounded-full" data-id="element-71">
                      {item.badge}
                    </span>}
                  {item.submenu && <span className={`transition-transform duration-200 ${expandedMenus.includes(item.id) ? 'rotate-0' : '-rotate-90'}`} data-id="element-72">
                      <ChevronDown size={16} className={isMenuActive(item.id) ? 'text-white' : 'text-gray-400'} data-id="element-73" />
                    </span>}
                </div>
              </button>

              {/* Submenu */}
              {item.submenu && expandedMenus.includes(item.id) && <div className="mt-1 ml-3 pl-6 border-l-2 border-gray-200 space-y-1" data-id="element-74">
                  {item.submenu.map(submenu => <button key={submenu.id} type="button" onClick={() => handleSubmenuClick(submenu)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${isSubmenuActive(submenu) ? 'bg-blue-50 text-bulog-blue font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-75">
                      <ChevronRight size={14} className="flex-shrink-0" data-id="element-76" />
                      <span className="truncate text-left" data-id="element-77">
                        {submenu.label}
                      </span>
                    </button>)}
                </div>}
            </div>)}
        </div>

        {/* Settings Section */}
        <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-78">
          <button type="button" onClick={() => onNavigate('settings')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === 'settings' ? 'bg-bulog-blue text-white shadow-md' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-79">
            <span className={`flex-shrink-0 ${currentPage === 'settings' ? 'text-white' : 'text-gray-500'}`} data-id="element-80">
              <Settings size={20} data-id="element-81" />
            </span>
            <span className="truncate text-left" data-id="element-82">Pengaturan</span>
          </button>
        </div>
      </nav>

      {/* User Profile & Logout */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0 bg-gray-50" data-id="element-83">
        <button type="button" onClick={onLogout} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-all duration-200 group border border-transparent hover:border-gray-200 hover:shadow-sm" data-id="element-84">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-bulog-blue to-bulog-blue-dark flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-85">
            <span className="font-bold text-sm text-white" data-id="element-86">AD</span>
          </div>
          <div className="flex-1 min-w-0 text-left" data-id="element-87">
            <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-88">
              Admin Pusat
            </p>
            <p className="text-xs text-gray-500 truncate" data-id="element-89">Divisi Perencanaan</p>
          </div>
          <LogOut size={18} className="text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-90" />
        </button>
      </div>
    </aside>;
}